import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { supabase } from "../supabaseClient";
import axios from "axios";
import { API_URL, ON_PREM } from "../globals";
import { CircularProgress } from "@mui/material";
import GoogleIcon from '@mui/icons-material/Google';

const defaultTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: '#ffffff',
    },
    background: {
      default: '#121212',
      paper: '#121212',
    },
    text: {
      primary: '#ffffff',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#121212',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          color: '#fff',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '8px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#94a3b8',
          '&.Mui-focused': {
            color: '#94a3b8',
          },
        },
      },
    },
  },
});

// Promotional component for the left side
const SignUpPromoComponent = () => {
  const steps = [
    {
      emoji: "🎬",
      title: "Upload Product Video",
      description: "Share a video walkthrough of your application"
    },
    {
      emoji: "🔔",
      title: "Join Slack Channel",
      description: "Join a dedicated Slack channel for your team"
    },
    {
      emoji: "📧",
      title: "Receive Results",
      description: "Immediately start running tests"
    }
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        pl: { xs: 4, md: 8, lg: 12 },
        pr: { xs: 4, md: 6 },
        py: 4,
        background: "linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(25, 25, 25, 0.9) 50%, rgba(20, 20, 25, 0.95) 100%)",
        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
        boxSizing: "border-box",
      }}
    >
      {/* Main Title */}
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            mb: 2,
            letterSpacing: "-0.02em",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            fontSize: { xs: "2rem", md: "2.5rem" },
            lineHeight: 1.2,
          }}
        >
          Get a Playwright Test Suite{" "}
          <span
            style={{
              textDecoration: "underline wavy",
              textDecorationColor: "#a855f7",
              textUnderlineOffset: "4px",
              textDecorationThickness: "2px",
            }}
          >
            Overnight
          </span>
        </Typography>
        
        <Typography
          variant="h6"
          sx={{
            color: "#94a3b8",
            fontWeight: 400,
            lineHeight: 1.5,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            fontSize: "1.1rem",
          }}
        >
          Convert your product videos into comprehensive Playwright test suites
        </Typography>
      </Box>

      {/* Steps */}
      <Box sx={{ mb: 5 }}>
        {steps.map((step, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              mb: 3,
              opacity: 0,
              animation: `fadeInUp 0.5s ease forwards ${index * 0.15 + 0.2}s`,
              "@keyframes fadeInUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(15px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <Box
              sx={{
                mr: 3,
                mt: 0.5,
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                fontSize: "18px",
              }}
            >
              {step.emoji}
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 600,
                  mb: 0.5,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  fontSize: "1.1rem",
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{
                  color: "#94a3b8",
                  lineHeight: 1.5,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  maxWidth: "420px",
                  fontSize: "1.05rem",
                }}
              >
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default function SignUp() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  async function generateCheckoutURL() {
    const authToken = (await supabase.auth.getSession()).data.session
      .access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(
        `${API_URL}/stripe/generate_checkout_url`,
        {
          headers: headers,
        }
      );
      console.log(response);
      return response.data.url;
    } catch (error) {
      console.log(error);
    }
  }

  async function signUpNewUser(email, password, firstName, lastName, company, position) {
    setLoading(true);
    try {
      // Send signup request to our backend API
      const response = await axios.post(`${API_URL}/signup`, {
        email,
        password,
        firstName,
        lastName,
        company,
        position
      });

      if (response.data.success) {
        // Sign in the user immediately after successful signup
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email,
          password: password,
        });

        if (error) {
          throw error;
        }

        // Store user data in localStorage
        const userData = {
          fullName: `${firstName} ${lastName}`,
          companyName: company,
          email: email,
          position: position
        };
        localStorage.setItem('onboardingUserData', JSON.stringify(userData));

        // Redirect to onboarding flow with user data
        navigate('/onboarding', {
          state: userData
        });
      }

    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.error || 'Error during signup process');
      setLoading(false);
    }
  }

  async function signUpWithGoogle() {
    setLoading(true);
    try {
      let { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) {
        console.log(error);
        alert('Error signing up with Google.');
        setLoading(false);
        return;
      }

      // The user will be redirected to Google first, and then we'll need to handle
      // the Stripe checkout in a separate component after they return
      // You might want to create a new component to handle the OAuth return
      // and subsequent Stripe checkout
    } catch (error) {
      console.error('Error:', error);
      alert('Error during Google signup');
      setLoading(false);
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    signUpNewUser(
      data.get("email"),
      data.get("password"),
      data.get("firstName"),
      data.get("lastName"),
      data.get("company"),
      data.get("title")
    );
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          backgroundColor: "#121212",
          display: "flex",
          overflow: "hidden",
        }}
      >
        {/* Left Half - Promotional Component */}
        <Box
          sx={{
            width: "50%",
            height: "100%",
            display: { xs: "none", md: "block" },
          }}
        >
          <SignUpPromoComponent />
        </Box>

        {/* Right Half - Sign Up Form */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            px: { xs: 3, sm: 4, md: 6 },
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "420px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: { xs: 3, sm: 4 },
              borderRadius: 3,
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.3),
                0 0 40px rgba(255, 255, 255, 0.05),
                0 0 80px rgba(255, 255, 255, 0.02),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `,
            }}
          >
            <Typography 
              component="h1" 
              variant="h4" 
              sx={{ 
                color: '#ffffff',
                fontWeight: 700,
                mb: 1,
                textAlign: 'center',
                letterSpacing: '-0.02em',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
              }}
            >
              Create Account
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: "#94a3b8",
                fontWeight: 400,
                mb: 4,
                textAlign: 'center',
                lineHeight: 1.6,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
              }}
            >
              Start your free trial today
            </Typography>

            <Box 
              component="form" 
              noValidate 
              onSubmit={handleSubmit} 
              sx={{ width: '100%' }}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    autoComplete="given-name"
                    name="firstName"
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    autoFocus
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    name="company"
                    required
                    fullWidth
                    id="company"
                    label="Company"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="title"
                    label="Title"
                    name="title"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
              
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <CircularProgress 
                    sx={{ 
                      color: '#ffffff',
                    }} 
                  />
                </Box>
              ) : (
                <>
                  <Button
                    type="submit"
                    fullWidth
                    sx={{
                      mt: 3,
                      mb: 2,
                      height: 44,
                      borderRadius: 2,
                      background: "#ffffff",
                      color: "#000000",
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                      boxShadow: "0 4px 16px rgba(255, 255, 255, 0.2)",
                      transition: 'all 0.3s ease',
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(255, 255, 255, 0.25)",
                        background: "#f8fafc",
                      },
                    }}
                  >
                    Create Account
                  </Button>
                </>
              )}
              
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#94a3b8',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                  }}
                >
                  Already have an account?{' '}
                  <Link 
                    href="/signin" 
                    sx={{ 
                      color: '#ffffff',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Sign in
                  </Link>
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  borderRadius: 2,
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    textAlign: 'center',
                    lineHeight: 1.5,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
                    display: 'block',
                  }}
                >
                  By creating an account, you agree to our Terms of Service and Privacy Policy.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
